import { ApolloQueryResult } from "@apollo/client";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";

import {
  CustomerFolderTypeCode,
  MappedStakeholder,
  QuestionnaireTypeCode,
} from "src/core/constants/folder";
import { Status } from "src/core/constants/folders";
import { CustomRiskLevel, getRiskLevelValue } from "src/core/constants/risk";
import { Roles } from "src/core/constants/roles";
import { useRiskOptions } from "src/core/contexts/RiskOptionsContext";
import { FolderFileQuery, Questionnaire } from "src/core/gql/graphql";
import { useStakeholdersDuplicateAutoCheck } from "src/core/hooks/folder/useStakeholdersDuplicateAutoCheck";
import { usePermissions } from "src/core/hooks/usePermissions";
import { mapContact, mapOwner } from "src/core/mappers/contact.mapper";
import { mapEntities } from "src/core/mappers/entity.mapper";
import { getFolderFieldValue } from "src/core/mappers/folder.mapper";
import { mapQuestionnaire } from "src/core/mappers/questionnaire.mapper";
import { mapToMappedStakeholdersWithAlertes } from "src/core/mappers/stakeholder.mapper";
import { Contact, Owner } from "src/core/types/contact";
import { MappedEntity } from "src/core/types/entity";
import {
  calculateAlerteMissingInfoEC,
  calculateAlerteMissingInfoNoAnalysis,
  calculateAlerteRiskLevel,
  calculateAllStakeholdersMissingInfo,
  calculateCompanyMissingInfo,
  calculateCorporationRiskLevel,
  calculateFolderMissingMissions,
  calculateFolderMissionsRiskLevel,
  calculateQuestionnaireMissingQuestions,
  calculateQuestionnaireRiskLevel,
  calculateStakeholdersMissingInfo,
  calculateStakeholdersRiskLevel,
} from "src/core/utils/folderAnalysis";

export type TabCounters = {
  global: number;
  corporation: number;
  stakeholders: number;
  stakeholdersDetails: Record<string, number>;
  questionnaire: number;
  alerte: number;
  missions: number;
};

export type TabRiskLevels = {
  corporation: CustomRiskLevel;
  stakeholders: CustomRiskLevel;
  questionnaire: CustomRiskLevel;
  alerte: CustomRiskLevel;
  missions: CustomRiskLevel;
};

export type StakeholdersCounter = {
  alerte: number;
  alerteNoAnalysis: number;
  totalStakeholdersWithInfoMissing: number;
  totalDocumentsMissing: number;
  details: Record<string, number>;
  global: number;
};

export type FolderContextType = {
  folderFile: FolderFileQuery;
  refetch: (variables?: { id: string }) => Promise<ApolloQueryResult<FolderFileQuery>>;
  mappedStakeholders: MappedStakeholder[];
  tabCounters: TabCounters;
  tabRiskLevels: TabRiskLevels;
  mappedEntities: MappedEntity[];
  isReadOnly?: boolean;
  stakeholdersCounter: StakeholdersCounter;
  isPPH: boolean;
  isDiligencesPrepared: boolean;
  /**
   * Dans le cas où l'utilisateur module le risque et que le formulaire n'est pas complété et enregistré
   * on veut afficher un message d'erreur dans les modales d'acceptation/refus.
   */
  isModulationInProgress: boolean;
  setIsModulationInProgress: (value: boolean) => void;
  isDiligencesCreated: boolean;
  setIsDiligencesCreated: (value: boolean) => void;
};

export const FolderContext = createContext<FolderContextType | undefined>(undefined);

type FolderProviderProps = {
  children: ReactNode;
  folderFile: FolderFileQuery;
  refetch: (variables?: { id: string }) => Promise<ApolloQueryResult<FolderFileQuery>>;
};

export const FolderProvider = ({ children, folderFile, refetch }: FolderProviderProps) => {
  const {
    countryWithRiskOptions,
    legalTypesWithRiskOptions,
    activityWithRiskOptions,
    missionTypeWithRiskOptions,
  } = useRiskOptions();

  const isPPH =
    folderFile.folderFile?.currentCustomerFolder?.customerFolderType?.code ===
    CustomerFolderTypeCode.PPH;

  const isDiligencesPrepared = useMemo<boolean>(() => {
    return folderFile.folderFile?.diligences.length && folderFile.folderFile?.diligences.length > 0
      ? true
      : false;
  }, [folderFile]);

  // Dans le cas où l'utilisateur module le risque et que le formulaire n'est pas complété et enregistré
  // on veut afficher un message d'erreur dans les modales d'acceptation/refus.
  const [isModulationInProgress, setIsModulationInProgress] = useState<boolean>(false);

  const baseMappedStakeholders = useMemo(() => {
    const raw = folderFile.folderFile?.fileCustomerFolder?.stakeholders;
    return raw
      ? mapToMappedStakeholdersWithAlertes(
          raw,
          countryWithRiskOptions,
          activityWithRiskOptions,
          legalTypesWithRiskOptions,
          folderFile,
          isPPH
        )
      : [];
  }, [
    folderFile,
    countryWithRiskOptions,
    activityWithRiskOptions,
    legalTypesWithRiskOptions,
    isPPH,
  ]);

  const isFolderInCompletion = folderFile?.folderFile?.workflowStatus?.state?.code === Status.DRAFT;

  const {
    duplicateCheckAutoByStakeholderId,
    duplicateCandidatesByStakeholderId,
    duplicateCheckLoadingByStakeholderId,
    duplicateCheckFetchedByStakeholderId,
  } = useStakeholdersDuplicateAutoCheck({
    folderId: folderFile.folderFile?.id,
    stakeholders: baseMappedStakeholders,
    enabled: isFolderInCompletion,
  });

  const mappedStakeholders = useMemo(() => {
    if (!baseMappedStakeholders.length) return [];

    return baseMappedStakeholders.map((s) => {
      if (s.isFolderCustomer) return s;

      const duplicateCheckDone = Boolean(
        s.duplicateCheckDone || duplicateCheckAutoByStakeholderId[s.id]
      );

      return {
        ...s,
        duplicateCheckDone,
        duplicateCandidates: duplicateCandidatesByStakeholderId[s.id],
        duplicateCheckLoading: duplicateCheckLoadingByStakeholderId[s.id],
        duplicateCheckFetched: duplicateCheckFetchedByStakeholderId[s.id] ?? false,
      };
    });
  }, [
    baseMappedStakeholders,
    duplicateCheckAutoByStakeholderId,
    duplicateCandidatesByStakeholderId,
    duplicateCheckLoadingByStakeholderId,
    duplicateCheckFetchedByStakeholderId,
  ]);

  const questionnaireLAB =
    folderFile.folderFile?.fileCustomerFolder?.supportingQuestionnaires?.find(
      (questionnaire) =>
        questionnaire.questionnaire?.type.code ===
        (isPPH ? QuestionnaireTypeCode.LAB_PPH : QuestionnaireTypeCode.LAB)
    );
  const mappedQuestionnaire = mapQuestionnaire(questionnaireLAB?.questionnaire as Questionnaire);

  // Mapping des entités pour récupérer les alertes PEP et sanctions.
  const mappedEntities = useMemo(
    () => mapEntities(folderFile, countryWithRiskOptions),
    [folderFile, countryWithRiskOptions]
  );

  const tabRiskLevels = useMemo(() => {
    const corporationRiskLevel = calculateCorporationRiskLevel(
      folderFile,
      countryWithRiskOptions,
      activityWithRiskOptions,
      legalTypesWithRiskOptions,
      isPPH
    );
    const stakeholdersRiskLevel = calculateStakeholdersRiskLevel(
      mappedStakeholders,
      countryWithRiskOptions
    );
    const questionnaireRiskLevel = calculateQuestionnaireRiskLevel(mappedQuestionnaire);
    const alerteRiskLevel = calculateAlerteRiskLevel(mappedEntities);
    const missionsRiskLevel = calculateFolderMissionsRiskLevel(
      folderFile,
      missionTypeWithRiskOptions
    );

    return {
      corporation: corporationRiskLevel,
      stakeholders: stakeholdersRiskLevel,
      questionnaire: questionnaireRiskLevel,
      alerte: alerteRiskLevel,
      missions: missionsRiskLevel,
    };
  }, [
    folderFile,
    mappedStakeholders,
    countryWithRiskOptions,
    activityWithRiskOptions,
    legalTypesWithRiskOptions,
    mappedQuestionnaire,
    mappedEntities,
    missionTypeWithRiskOptions,
    isPPH,
  ]);
  const { hasRole } = usePermissions();
  const isReadOnly =
    folderFile?.folderFile?.workflowStatus?.state?.code !== Status.DRAFT || hasRole(Roles.AUDITEUR);
  const [isDiligencesCreated, setIsDiligencesCreated] = useState<boolean>(false);
  const tabCounters = useMemo(() => {
    const corporation = isPPH ? 0 : calculateCompanyMissingInfo(folderFile);
    const { total: stakeholders, details: stakeholdersDetails } = calculateStakeholdersMissingInfo(
      folderFile,
      isPPH
    );
    const questionnaire = calculateQuestionnaireMissingQuestions(mappedQuestionnaire);
    const alerte = calculateAlerteMissingInfoEC(mappedEntities);
    const missions = calculateFolderMissingMissions(folderFile, missionTypeWithRiskOptions);

    return {
      global: corporation + stakeholders + questionnaire + alerte + missions,
      corporation,
      stakeholders,
      stakeholdersDetails,
      questionnaire,
      alerte,
      missions,
    };
  }, [folderFile, mappedQuestionnaire, mappedEntities, missionTypeWithRiskOptions, isPPH]);

  const stakeholdersCounter = useMemo(() => {
    const alerte = calculateAlerteMissingInfoEC(mappedEntities);
    const alerteNoAnalysis = calculateAlerteMissingInfoNoAnalysis(mappedEntities);
    const {
      totalStakeholdersWithInfoMissing,
      totalDocumentsMissing,
      details,
      isLegalRepresentativeMissing,
    } = calculateAllStakeholdersMissingInfo(folderFile, isPPH);

    const global =
      alerte +
      totalStakeholdersWithInfoMissing +
      totalDocumentsMissing +
      (isLegalRepresentativeMissing && !isPPH ? 1 : 0) +
      alerteNoAnalysis;

    return {
      alerte,
      alerteNoAnalysis,
      totalStakeholdersWithInfoMissing,
      totalDocumentsMissing,
      details,
      global,
    };
  }, [mappedEntities, folderFile, isPPH]);

  return (
    <FolderContext.Provider
      value={{
        folderFile,
        refetch,
        mappedStakeholders,
        tabCounters,
        tabRiskLevels,
        mappedEntities,
        isReadOnly,
        stakeholdersCounter,
        isPPH,
        isModulationInProgress,
        setIsModulationInProgress,
        isDiligencesPrepared,
        isDiligencesCreated,
        setIsDiligencesCreated,
      }}
    >
      {children}
    </FolderContext.Provider>
  );
};

/**
 * Hook pour accéder au contexte du dossier
 * @throws {Error} Si utilisé en dehors d'un FolderProvider
 */
export const useFolder = (): FolderContextType => {
  const context = useContext(FolderContext);
  if (context === undefined) {
    throw new Error("useFolder must be used within a FolderProvider");
  }
  return context;
};

/**
 * Hook pour récupérer l'ID de l'entreprise du dossier
 */
export const useFolderCorporationId = (): string | undefined => {
  const { folderFile } = useFolder();
  return folderFile.folderFile?.fileCustomerFolder?.customer?.id;
};

/**
 * Hook pour récupérer l'ID de l'entreprise.
 * Utilisé pour ajouter ou supprimer l'expert comptable du dossier (owner).
 */
export const useFolderCustomerId = (): string | undefined => {
  const { folderFile } = useFolder();
  return folderFile.folderFile?.fileCustomerFolder?.customer?.id;
};

/**
 * Hook pour récupérer l'ID du dossier de l'entreprise.
 */
export const useFolderCustomerFolderId = (): string | undefined => {
  const { folderFile } = useFolder();
  return folderFile.folderFile?.fileCustomerFolder?.id;
};

/**
 * Hook pour récupérer l'ID du currentCustomerFolder.
 * Si la fiche n'est plus éditable, il faut utiliser currentCustomerFolder afin de pouvoir éditer
 * l'owner et le contactPerson.
 */
export const useCurrentCustomerFolderId = (): string | undefined => {
  const { folderFile } = useFolder();
  return folderFile.folderFile?.currentCustomerFolder?.id;
};

/**
 * Hook pour récupérer les informations des personnes associées au dossier (propriétaire et contact)
 */
export const useFolderPersons = (): {
  owner: Owner | null;
  contactPerson: Contact | null;
} => {
  const { folderFile } = useFolder();

  const owner = mapOwner(folderFile);
  const contactPerson = mapContact(folderFile);

  return { owner, contactPerson };
};

/**
 * Hook pour récupérer la raison sociale de l'entreprise
 */
export const useFolderCompanyName = (): string | undefined => {
  const { folderFile } = useFolder();
  return (
    folderFile.folderFile?.fileCustomerFolder?.customer?.allInputValues?.find(
      (iv: { inputField: { fieldCode: string }; stringValue?: string | null }) =>
        iv.inputField.fieldCode === "name"
    )?.stringValue ?? undefined
  );
};

/**
 * Hook pour récupérer les entités du dossier
 */
export const useFolderEntities = (): MappedEntity[] => {
  const { mappedEntities } = useFolder();

  // On tri d'abord par PPH, puis par nom de famille et enfin par PMO.
  const sortedEntities = mappedEntities.sort((a, b) => {
    if (a.isPPH && !b.isPPH) return -1;
    if (!a.isPPH && b.isPPH) return 1;
    if (a.lastName && !b.lastName) return -1;
    if (!a.lastName && b.lastName) return 1;
    return 0;
  });

  return sortedEntities;
};

/**
 * Hook pour récupérer la version courante du dossier
 */
export const useFolderCurrentVersion = () => {
  const { folderFile } = useFolder();
  const summaries = folderFile.folderFile?.currentCustomerFolder?.folderFiles?.summaries ?? [];

  const currentVersion = (() => {
    // D'abord chercher un fichier avec le statut DRAFT
    const draftFile = summaries.find((file) => file.workflowStatus?.state?.code === Status.DRAFT);

    if (draftFile) {
      return draftFile;
    }

    // Si aucun fichier DRAFT, prendre celui avec la date de modification la plus récente
    return summaries.reduce((latest, current) => {
      const latestDate = latest.workflowStatus?.lastModification;
      const currentDate = current.workflowStatus?.lastModification;

      if (!latestDate) return current;
      if (!currentDate) return latest;

      return new Date(currentDate) > new Date(latestDate) ? current : latest;
    });
  })();

  return currentVersion;
};

export type CartographyRisks = {
  activity: CustomRiskLevel;
  location: CustomRiskLevel;
  client: CustomRiskLevel;
  mission: CustomRiskLevel;
};

/**
 * Hook pour récupérer les risques du dossier pour l'onglet cartographie.
 */
export const useCartographyRisks = (): CartographyRisks => {
  const { folderFile, mappedStakeholders, mappedEntities, tabRiskLevels, isPPH } = useFolder();
  const { activityWithRiskOptions, countryWithRiskOptions, legalTypesWithRiskOptions } =
    useRiskOptions();

  // Données liées à l'activité
  const activityCode = getFolderFieldValue(folderFile, "activityCode");
  const activityMatch = activityWithRiskOptions.find((a) => a.value === activityCode);

  // Données liées à la localisation
  const paysImmatriculation = getFolderFieldValue(folderFile, "registrationCountryCode");
  const paysImmatriculationMatch = countryWithRiskOptions.find(
    (c) => c.value === paysImmatriculation
  );

  let riskLevelLocation: CustomRiskLevel = CustomRiskLevel.UNDEFINED;
  if (paysImmatriculationMatch) {
    riskLevelLocation = paysImmatriculationMatch.risk;
  }
  mappedStakeholders.forEach((stakeholder) => {
    if (stakeholder.isFolderCustomer && !isPPH) {
      return;
    }
    const paysResidenceMatchingCountry = countryWithRiskOptions.find(
      (country) => country.value === stakeholder.residenceCountryCode
    );
    if (paysResidenceMatchingCountry) {
      const riskLevelStakeholder = paysResidenceMatchingCountry.risk;
      if (getRiskLevelValue(riskLevelStakeholder) > getRiskLevelValue(riskLevelLocation)) {
        riskLevelLocation = riskLevelStakeholder;
      }
    }
  });

  // Données liées au client
  const typeJuridique = getFolderFieldValue(folderFile, "entityTypeCode");
  const typeJuridiqueMatch = legalTypesWithRiskOptions.find((type) => type.value === typeJuridique);

  let riskLevelClient: CustomRiskLevel = CustomRiskLevel.UNDEFINED;
  if (typeJuridiqueMatch) {
    riskLevelClient = typeJuridiqueMatch.risk;
  }

  mappedEntities.forEach((entity) => {
    const riskLevelEntity = entity.riskLevel;
    if (getRiskLevelValue(riskLevelEntity) > getRiskLevelValue(riskLevelClient)) {
      riskLevelClient = riskLevelEntity;
    }
  });
  if (
    tabRiskLevels.questionnaire &&
    getRiskLevelValue(tabRiskLevels.questionnaire) > getRiskLevelValue(riskLevelClient)
  ) {
    riskLevelClient = tabRiskLevels.questionnaire;
  }

  return {
    activity: activityMatch?.risk ?? CustomRiskLevel.UNDEFINED,
    location: riskLevelLocation,
    client: riskLevelClient,
    mission: tabRiskLevels.missions,
  };
};
